import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { salesService } from '../../../services/salesService';
import { expenseService } from '../../../services/expenseService';
import { financeInsightsService, FinanceInsightInput } from '../../../services/financeInsightsService';

const fmt = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(n);

const InsightsTab: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [periodDays, setPeriodDays] = useState(30);
  const [sales, setSales] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - periodDays + 1);
        const [s, e] = await Promise.all([
          salesService.list({ from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10), limit: 500 }),
          expenseService.list({ from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10), limit: 500 }),
        ]);
        setSales(s);
        setExpenses(e);
      } catch (err: any) {
        console.error('Insights load error', err);
        setError(err?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [periodDays]);

  const { revenue, spend, grossProfit, marginPct, dailyBurn, runwayDays } = useMemo(() => {
    const revenue = sales.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const spend = expenses.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const grossProfit = revenue - spend;
    const marginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
    const dailyBurn = spend / Math.max(1, periodDays);
    const runwayDays = grossProfit > 0 ? Infinity : (revenue > 0 ? Math.round(Math.abs(grossProfit) / Math.max(1, dailyBurn)) : 0);
    return { revenue, spend, grossProfit, marginPct, dailyBurn, runwayDays };
  }, [sales, expenses, periodDays]);

  const recommendations = useMemo(() => {
    const recs: { title: string; detail?: string; severity: 'critical' | 'warning' | 'info'; actionRoute?: string; actionLabel?: string }[] = [];
    
    // Critical issues
    if (revenue === 0 && expenses.length > 0) {
      recs.push({
        title: 'No revenue recorded',
        detail: 'You have expenses but no sales recorded in this period. Start tracking your sales to get accurate insights.',
        severity: 'critical',
        actionRoute: '/finance-hub#transactions',
        actionLabel: 'Add Sales'
      });
    }

    if (spend > revenue && revenue > 0) {
      recs.push({
        title: 'Expenses exceed revenue',
        detail: `Your expenses (${fmt(spend)}) are higher than revenue (${fmt(revenue)}). Consider cost reduction or increasing sales volume.`,
        severity: 'critical',
        actionRoute: '/finance-hub#transactions',
        actionLabel: 'Review Expenses'
      });
    }

    // Warnings
    if (marginPct < 20 && marginPct > 0) {
      recs.push({
        title: 'Low profit margin',
        detail: `Your current margin is ${marginPct.toFixed(1)}%. Consider reviewing your pricing strategy or reducing costs.`,
        severity: 'warning',
        actionRoute: '/finance/pricing-strategy',
        actionLabel: 'Pricing Strategy'
      });
    }

    if (marginPct >= 20 && marginPct < 40) {
      recs.push({
        title: 'Moderate profit margin',
        detail: `Your margin of ${marginPct.toFixed(1)}% is acceptable, but there's room for improvement through pricing optimization or cost efficiency.`,
        severity: 'info',
        actionRoute: '/finance/pricing-strategy',
        actionLabel: 'Optimize Pricing'
      });
    }

    if (runwayDays < 90 && runwayDays > 0 && runwayDays !== Infinity) {
      recs.push({
        title: 'Limited runway',
        detail: `At current burn rate, you have approximately ${runwayDays} days of runway. Focus on increasing revenue or reducing expenses.`,
        severity: 'warning',
        actionRoute: '/finance/cash-flow',
        actionLabel: 'Cash Flow'
      });
    }

    // Positive insights
    if (marginPct >= 40) {
      recs.push({
        title: 'Healthy profit margins',
        detail: `Your ${marginPct.toFixed(1)}% margin is strong. Consider reinvesting in growth or building cash reserves.`,
        severity: 'info'
      });
    }

    if (revenue > spend && marginPct >= 30) {
      recs.push({
        title: 'Strong financial position',
        detail: 'Your business is profitable with healthy margins. Consider scaling operations or exploring new opportunities.',
        severity: 'info'
      });
    }

    // Fallback
    if (recs.length === 0) {
      recs.push({
        title: 'Keep tracking your finances',
        detail: 'Continue recording your transactions for better insights and recommendations.',
        severity: 'info',
        actionRoute: '/finance-hub#transactions',
        actionLabel: 'Add Transactions'
      });
    }

    return recs;
  }, [marginPct, spend, revenue, periodDays, runwayDays]);

  const saveSnapshot = async () => {
    try {
      setSaving(true);
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - periodDays + 1);
      const payload: FinanceInsightInput = {
        period_from: from.toISOString().slice(0,10),
        period_to: to.toISOString().slice(0,10),
        summary: { revenue, spend, grossProfit, marginPct },
        kpis: { dailyBurn, runwayDays },
        recommendations: recommendations.map(r => ({ title: r.title, detail: r.detail })),
      };
      await financeInsightsService.create(payload);
      toast.success('Financial snapshot saved');
    } catch (e: any) {
      console.error('saveSnapshot error', e);
      toast.error(e?.message || 'Failed to save snapshot');
    } finally {
      setSaving(false);
    }
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: '🚨',
          iconBg: 'bg-red-100',
          title: 'text-red-900',
          detail: 'text-red-700'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          icon: '⚠️',
          iconBg: 'bg-amber-100',
          title: 'text-amber-900',
          detail: 'text-amber-700'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: '💡',
          iconBg: 'bg-blue-100',
          title: 'text-blue-900',
          detail: 'text-blue-700'
        };
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-100">
        <div className="text-gray-500">Analyzing your financial data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-6 text-red-700 shadow-sm ring-1 ring-red-100">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">AI-Powered Insights</h3>
          <p className="mt-1 text-sm text-gray-600">Personalized recommendations based on your financial data</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={saveSnapshot}
            disabled={saving}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : '💾 Save Snapshot'}
          </button>
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 p-6 shadow-sm ring-1 ring-purple-100">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="text-base font-semibold text-purple-900">Period Summary</h4>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-purple-700">Revenue:</span>
                <span className="font-semibold text-purple-900">{fmt(revenue)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-purple-700">Expenses:</span>
                <span className="font-semibold text-purple-900">{fmt(spend)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-purple-700">Net:</span>
                <span className={`font-semibold ${grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {fmt(grossProfit)}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-purple-700">Margin</div>
            <div className="mt-1 text-3xl font-bold text-purple-900">{marginPct.toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="space-y-4">
        <h4 className="text-base font-semibold text-gray-900">Recommendations</h4>
        {recommendations.map((rec, idx) => {
          const styles = getSeverityStyles(rec.severity);
          return (
            <div
              key={idx}
              className={`rounded-xl border ${styles.border} ${styles.bg} p-6 shadow-sm transition hover:shadow-md`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${styles.iconBg}`}>
                  <span className="text-xl">{styles.icon}</span>
                </div>
                <div className="flex-1">
                  <h5 className={`text-base font-semibold ${styles.title}`}>{rec.title}</h5>
                  {rec.detail && (
                    <p className={`mt-2 text-sm ${styles.detail}`}>{rec.detail}</p>
                  )}
                  {rec.actionRoute && (
                    <button
                      onClick={() => navigate(rec.actionRoute!)}
                      className={`mt-3 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        rec.severity === 'critical'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : rec.severity === 'warning'
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {rec.actionLabel || 'Take Action'} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InsightsTab;
